import { useState, useRef } from "react";
import { toast } from "sonner";
import { Upload, X, Copy } from "lucide-react";
import { MARKER_SHAPES, SHAPE_PRESETS } from "@/lib/markerShapes";

const TYPES = [
  { key: "poles", label: "Słup", presets: ["●", "▮", "┃", "✚", "◉"] },
  { key: "closures", label: "Mufa", presets: ["◆", "■", "⬢", "▲", "◈"] },
  { key: "olt", label: "OLT", presets: ["▣", "◈", "☰", "⬛", "◑"] },
  { key: "odf", label: "Szafa / ODF", presets: ["▦", "☰", "▤", "▥", "▣"] },
  { key: "splitters", label: "Spliter", presets: ["⧉", "⋔", "✳", "◑", "⬥"] },
  { key: "onu", label: "ONU", presets: ["◉", "●", "⬤", "◍", "◎"] },
];
const PREVIEW = "#38bdf8";
const DEF_COLORS = { poles: "#EAB308", closures: "#A855F7", olt: "#3B82F6", odf: "#10B981", splitters: "#F97316", onu: "#06B6D4" };

function Preview({ spec, color }) {
  const c = color || PREVIEW;
  if (spec?.img) return <img src={spec.img} alt="" className="w-7 h-7 object-contain" />;
  if (spec?.shape && MARKER_SHAPES[spec.shape]) {
    return <span className="w-7 h-7 inline-flex items-center justify-center" dangerouslySetInnerHTML={{ __html: MARKER_SHAPES[spec.shape](c) }} />;
  }
  if (spec?.glyph && spec?.noCircle) {
    return <span className="w-7 h-7 inline-flex items-center justify-center text-xl font-extrabold" style={{ color: c }}>{spec.glyph}</span>;
  }
  return <span className="w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold" style={{ background: c, color: "#0b1120" }}>{spec?.glyph || "●"}</span>;
}

export function MarkerIconSettings({ settings, save }) {
  const [icons, setIcons] = useState({ ...(settings.marker_icons || {}) });
  const [colors, setColors] = useState({ ...DEF_COLORS, ...(settings.marker_colors || {}) });
  const [busy, setBusy] = useState(false);
  const [scale, setScale] = useState(Number(settings.marker_scale) || 1);
  const [zmin, setZmin] = useState(Number(settings.marker_zoom_min) || 0.12);
  const [zmax, setZmax] = useState(Number(settings.marker_zoom_max) || 1);
  const [sizes, setSizes] = useState({ ...(settings.marker_size_type || {}) });
  const [showLegend, setShowLegend] = useState(settings.show_legend !== false);
  const [showCableLen, setShowCableLen] = useState(settings.show_cable_length === true);
  const fileRefs = useRef({});
  const setShape = (k, id) => setIcons((s) => ({ ...s, [k]: { shape: id } }));
  const setGlyph = (k, g) => setIcons((s) => ({ ...s, [k]: { glyph: g, noCircle: s[k]?.noCircle } }));
  const setNoCircle = (k, v) => setIcons((s) => ({ ...s, [k]: { ...(s[k] || {}), noCircle: v } }));
  const setColor = (k, v) => setColors((c) => ({ ...c, [k]: v }));
  const clear = (k) => setIcons((s) => { const n = { ...s }; delete n[k]; return n; });
  const copyToAll = (k) => {
    const spec = icons[k];
    const col = colors[k];
    const sz = sizes[k] ?? 1;
    setColors((c) => ({ ...c, ...Object.fromEntries(TYPES.map((tp) => [tp.key, col])) }));
    setSizes((s) => ({ ...s, ...Object.fromEntries(TYPES.map((tp) => [tp.key, sz])) }));
    setIcons((s) => { const n = { ...s }; TYPES.forEach((tp) => { if (spec) n[tp.key] = { ...spec }; else delete n[tp.key]; }); return n; });
    toast.success(`Zastosowano styl „${TYPES.find((tt) => tt.key === k)?.label}" do wszystkich typów`);
  };
  const onFile = (k, file) => {
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error("Ikona za duża (max 500 KB)"); return; }
    const r = new FileReader();
    r.onload = () => setIcons((s) => ({ ...s, [k]: { img: r.result } }));
    r.readAsDataURL(file);
  };
  const submit = async () => {
    setBusy(true);
    try { await save({ marker_icons: icons, marker_colors: colors, marker_scale: scale, marker_size_type: sizes, marker_zoom_min: zmin, marker_zoom_max: zmax, show_legend: showLegend, show_cable_length: showCableLen }); toast.success("Zapisano ustawienia mapy"); }
    catch { toast.error("Błąd zapisu"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-4">
      <p className="text-xs text-slate-400">Wybierz gotowy kształt (zmienia cały wygląd markera), symbol, lub wgraj własną ikonę (PNG/SVG, max 500 KB). Zmiany widać na mapie po zapisaniu.</p>
      <div className="border border-slate-700 rounded-lg p-3" data-testid="markericon-size-row">
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-sm font-medium">Rozmiar markerów na mapie</span>
          <span className="text-xs font-mono text-cyan-300" data-testid="markericon-size-value">{Math.round(scale * 100)}%</span>
        </div>
        <input type="range" min="0.7" max="1.8" step="0.05" value={scale}
          onChange={(e) => setScale(Number(e.target.value))} data-testid="markericon-size-slider"
          className="w-full accent-cyan-500 cursor-pointer" />
        <div className="flex justify-between text-[10px] text-slate-500 mt-0.5"><span>mniejsze</span><span>domyślne</span><span>większe</span></div>
      </div>
      <div className="border border-slate-700 rounded-lg p-3" data-testid="markericon-zoom-row">
        <div className="text-sm font-medium mb-2">Skalowanie kropek i klastrów wg oddalenia mapy</div>
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[11px] text-slate-500 w-40">Min. rozmiar (mapa oddalona)</span>
          <input type="range" min="0.05" max="0.6" step="0.01" value={zmin}
            onChange={(e) => setZmin(Math.min(Number(e.target.value), zmax - 0.05))} data-testid="markericon-zoommin-slider"
            className="flex-1 accent-cyan-500 cursor-pointer" />
          <span className="text-[11px] font-mono text-cyan-300 w-10 text-right" data-testid="markericon-zoommin-value">{Math.round(zmin * 100)}%</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-slate-500 w-40">Maks. rozmiar (mapa przybliżona)</span>
          <input type="range" min="0.7" max="1.6" step="0.05" value={zmax}
            onChange={(e) => setZmax(Math.max(Number(e.target.value), zmin + 0.05))} data-testid="markericon-zoommax-slider"
            className="flex-1 accent-cyan-500 cursor-pointer" />
          <span className="text-[11px] font-mono text-cyan-300 w-10 text-right" data-testid="markericon-zoommax-value">{Math.round(zmax * 100)}%</span>
        </div>
        <p className="text-[10px] text-slate-500 mt-1.5">Im mniejszy „min", tym drobniejsze kropki i klastry przy dużym oddaleniu mapy.</p>
      </div>
      <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="legend-toggle-label">
        <input type="checkbox" checked={showLegend} onChange={(e) => setShowLegend(e.target.checked)} data-testid="legend-toggle" className="accent-cyan-500" />
        Pokaż legendę na mapie
      </label>
      <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="cable-length-toggle-label">
        <input type="checkbox" checked={showCableLen} onChange={(e) => setShowCableLen(e.target.checked)} data-testid="cable-length-toggle" className="accent-cyan-500" />
        Pokaż długość kabli na stałe przy liniach
      </label>
      {TYPES.map((tp) => (
        <div key={tp.key} className="border border-slate-700 rounded-lg p-3 space-y-2" data-testid={`markericon-row-${tp.key}`}>
          <div className="flex items-center gap-2">
            <Preview spec={icons[tp.key]} color={colors[tp.key]} />
            <span className="text-sm font-medium flex-1">{tp.label}</span>
            <input type="color" value={colors[tp.key] || "#38bdf8"} onChange={(e) => setColor(tp.key, e.target.value)}
              data-testid={`markericon-color-${tp.key}`} title="Kolor symbolu"
              className="w-9 h-8 rounded bg-transparent border border-slate-600 cursor-pointer shrink-0" />
            <button onClick={() => copyToAll(tp.key)} data-testid={`markericon-copyall-${tp.key}`}
              className="text-slate-400 hover:text-cyan-300 shrink-0" title="Zastosuj ten kolor i kształt do wszystkich typów"><Copy size={15} /></button>
            {icons[tp.key] && (
              <button onClick={() => clear(tp.key)} data-testid={`markericon-clear-${tp.key}`}
                className="text-slate-400 hover:text-red-400" title="Przywróć domyślną"><X size={16} /></button>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] text-slate-500 w-full">Kształty:</span>
            {(SHAPE_PRESETS[tp.key] || []).map((id) => (
              <button key={id} onClick={() => setShape(tp.key, id)} data-testid={`markericon-shape-${tp.key}-${id}`}
                className={`w-9 h-9 rounded-md flex items-center justify-center border transition-colors ${icons[tp.key]?.shape === id ? "bg-slate-900 border-cyan-400 ring-1 ring-cyan-400" : "bg-slate-700 border-slate-600 hover:bg-slate-600"}`}
                dangerouslySetInnerHTML={{ __html: MARKER_SHAPES[id](icons[tp.key]?.shape === id ? "#38bdf8" : "#94a3b8") }} />
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-[11px] text-slate-500 w-full">Symbole:</span>
            {tp.presets.map((g) => (
              <button key={g} onClick={() => setGlyph(tp.key, g)} data-testid={`markericon-preset-${tp.key}-${g}`}
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold border transition-colors ${icons[tp.key]?.glyph === g ? "bg-cyan-500 text-slate-900 border-cyan-400" : "bg-slate-700 text-cyan-300 border-slate-600 hover:bg-slate-600"}`}>{g}</button>
            ))}
            <button onClick={() => fileRefs.current[tp.key]?.click()} data-testid={`markericon-upload-${tp.key}`}
              className="h-8 px-2 rounded-md bg-slate-700 hover:bg-slate-600 text-xs flex items-center gap-1 border border-slate-600"><Upload size={13} /> Wgraj</button>
            <input ref={(el) => (fileRefs.current[tp.key] = el)} type="file" accept="image/*" className="hidden"
              onChange={(e) => onFile(tp.key, e.target.files?.[0])} />
          </div>
          <label className="flex items-center gap-2 text-[11px] text-slate-400 cursor-pointer" data-testid={`markericon-nocircle-label-${tp.key}`}>
            <input type="checkbox" checked={!!icons[tp.key]?.noCircle} onChange={(e) => setNoCircle(tp.key, e.target.checked)}
              data-testid={`markericon-nocircle-${tp.key}`} className="accent-cyan-500" />
            Symbol bez tła (samo kółka/trójkąt bez wypełnionego okręgu)
          </label>
          <div className="flex items-center gap-2 pt-1">
            <span className="text-[11px] text-slate-500">Rozmiar:</span>
            <input type="range" min="0.5" max="2" step="0.1" value={sizes[tp.key] ?? 1}
              onChange={(e) => setSizes((s) => ({ ...s, [tp.key]: Number(e.target.value) }))}
              data-testid={`markericon-typesize-${tp.key}`} className="flex-1 accent-cyan-500 cursor-pointer" />
            <span className="text-[11px] font-mono text-cyan-300 w-10 text-right">{Math.round((sizes[tp.key] ?? 1) * 100)}%</span>
          </div>
        </div>
      ))}
      <button onClick={submit} disabled={busy} data-testid="markericon-save"
        className="w-full bg-blue-600 hover:bg-blue-700 rounded px-3 py-2 text-sm font-medium disabled:opacity-50">
        {busy ? "Zapisywanie…" : "Zapisz ikony i rozmiar"}
      </button>
    </div>
  );
}
